"use client";

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useRef, useState, type ReactNode } from "react";
import { chunk } from "remeda";

import { cn, swipePower, wrap } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleLeftSmallIcon } from "~/components/icons/angle-left-small";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { AngleRightSmallIcon } from "~/components/icons/angle-right-small";
import { H2 } from "~/components/typography";
import { Button } from "~/components/ui/button";

import { Viewport } from "./sm";

type Direction = -1 | 1;
const swipeConfidenceThreshold = 10_000;

interface Props<T> {
  CardComponent: (props: T) => ReactNode;
  className?: string;
  items: T[];
  title: ReactNode;
}

function MobileCards<T extends { id: string | number }>({
  items,
  CardComponent,
  title,
}: Props<T>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const tAccessibility = useTranslations("Accessibility");

  return (
    <div>
      <div>
        <div className="mb-4 flex items-start justify-between">
          {typeof title === "string" ? <H2>{title}</H2> : title}
          <div className="flex justify-end gap-1">
            <Button
              size="circle"
              onClick={(event) => {
                event.preventDefault();
                if (ref.current) {
                  ref.current.scrollBy({
                    left: -1 * (288 + 24),
                    behavior: "smooth",
                  });
                }
              }}
            >
              <AngleLeftSmallIcon />
              <span className="sr-only">{tAccessibility("left")}</span>
            </Button>
            <Button
              size="circle"
              onClick={(event) => {
                event.preventDefault();
                if (ref.current) {
                  ref.current.scrollBy({
                    left: 288 + 24,
                    behavior: "smooth",
                  });
                }
              }}
            >
              <AngleRightSmallIcon />
              <span className="sr-only">{tAccessibility("right")}</span>
            </Button>
          </div>
        </div>
      </div>
      <div className="-mx-6 max-w-[100vw] overflow-hidden">
        <div
          className="flex snap-x snap-mandatory justify-between overflow-x-auto overflow-y-hidden"
          ref={ref}
        >
          {items.map((item) => (
            <div
              className="box-content w-72 shrink-0 snap-start pb-6 pl-6 last:pr-24"
              key={item.id}
            >
              <CardComponent {...item} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DesktopCards<T extends { id: string | number }>({
  items,
  CardComponent,
  title,
  className,
}: Props<T>) {
  const chunks = chunk(items, 3);
  const tAccessibility = useTranslations("Accessibility");

  const [[page, direction], setPage] = useState([0, 0]);
  const index = wrap(0, chunks.length, page);

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        {typeof title === "string" ? <H2>{title}</H2> : title}

        {items.length > 3 ? (
          <div className="flex justify-end gap-1">
            <Button
              disabled={index === 0}
              onClick={(event) => {
                event.preventDefault();
                paginate(-1);
              }}
              size="circle"
            >
              <span className="sr-only">{tAccessibility("left")}</span>
              <AngleLeftIcon />
            </Button>
            <Button
              disabled={index >= chunks.length - 1}
              onClick={(event) => {
                event.preventDefault();
                paginate(1);
              }}
              size="circle"
            >
              <span className="sr-only">{tAccessibility("right")}</span>
              <AngleRightIcon />
            </Button>
          </div>
        ) : null}
      </div>

      {/* Desktop */}
      <div className="block w-full overflow-hidden">
        <AnimatePresence custom={direction} mode="popLayout" initial={false}>
          <motion.div
            animate="center"
            className={cn("grid grid-cols-3 gap-8", className)}
            custom={direction}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            exit="exit"
            initial="initial"
            key={`chunk-${index}`}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            transition={{
              opacity: { duration: 0.2 },
              x: { damping: 30, stiffness: 300, type: "spring" },
            }}
            variants={{
              center: {
                opacity: 1,
                x: 0,
              },
              exit: (direction: Direction) => ({
                opacity: [1, 1, 0],
                x: direction === 1 ? "-100%" : "100%",
              }),
              initial: (direction: Direction) => ({
                opacity: 1,
                x: direction === 1 ? "110%" : "-110%",
              }),
            }}
          >
            {chunks[index]?.map((props) => (
              <CardComponent key={props.id} {...props} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export function Cards<T extends { id: string | number }>(props: Props<T>) {
  return (
    <Viewport
      desktop={<DesktopCards {...props} />}
      mobile={<MobileCards {...props} />}
    />
  );
}
